<template>
    <!-- app/javascript/dashboard/components/SuccessModal.vue -->
    <div class="modal-backdrop" @click.self="closeModal">
        <div class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-[37.5rem]">
            <!-- Changed from custom CSS to Tailwind classes with dark mode variant -->
            <div class="modal-header bg-green-500 dark:bg-green-400">
                <h3 class="modal-title"></h3>
                <button
                    class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
                    @click="closeModal">
                    <span class="i-lucide-x flex-shrink-0 text-white"></span>
                </button>
            </div>

            <div class="modal-body text-center">
                <!-- Success circle with checkmark and ripple effect -->
                <div class="success-icon">
                    <div class="circle-container">
                        <!-- Changed to use Tailwind classes -->
                        <div class="ripple-circle bg-green-400/30 dark:bg-green-300/30"></div>
                        <div class="green-circle pop-animation bg-green-500 dark:bg-green-400">
                            <span class="i-lucide-check text-white"></span>
                        </div>
                    </div>
                </div>
                
                <!-- Success message -->
                <h4 class="success-title">{{ successTitle }}</h4>
                <p class="success-message text-slate-600 dark:text-slate-300">
                    {{ successMessage }}
                </p>
            </div>

            <div class="modal-footer border-t-2 border-n-slate-4 dark:border-n-slate-7">
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="closeModal">
                    Finalizar
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        customTitle: {
            type: String,
            default: 'Tudo certo!'
        },
        customMessage: {
            type: String,
            default: ''
        }
    },
    emits: ['close'],
    setup(props, { emit }) {
        const closeModal = () => {
            emit('close');
        };

        // Simplified computed property for message
        const successMessage = computed(() => {
            if (props.customMessage) return props.customMessage;
            return 'Operação concluída com sucesso!';
        });

        // Add computed property for title
        const successTitle = computed(() => {
            return props.customTitle || 'Tudo certo!';
        });

        return {
            closeModal,
            successMessage,
            successTitle
        };
    }
};
</script>

<style scoped>
:root {
    --light-primary-hover: #dddddd;
    --transition-speed: 0.2s;
}

/* Modal styles */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.modal-container {
    border-radius: 8px;
    width: 500px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    /* Removed background-color from here - now using Tailwind classes */
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.modal-body {
    padding: 24px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid rgb(var(--slate-4) / 1);
}

.dark .modal-footer {
    border-top: 1px solid rgb(var(--slate-6) / 1);
}

/* Success styles */
.success-icon {
    margin: 0 auto 16px;
}

.circle-container {
    position: relative;
    width: 128px;
    height: 128px;
    margin: 0 auto 16px;
}

.green-circle {
    width: 128px;
    height: 128px;
    /* Background color now controlled by Tailwind classes */
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 2;
}

.ripple-circle {
    position: absolute;
    top: 0;
    left: 0;
    width: 128px;
    height: 128px;
    border-radius: 50%;
    /* Background color now controlled by Tailwind classes */
    opacity: 0;
    z-index: 1;
    animation: ripple 0.6s ease-in-out;
    animation-delay: 0.2s; /* Start when green circle is at scale 0.9 */
}

.green-circle span {
    font-size: 108px;
}

/* Pop animation */
.pop-animation {
    animation: pop 0.6s ease-in-out;
}

@keyframes pop {
    0% {
        transform: scale(0.5);
    }
    50% {
        transform: scale(1.2);
    }
    75% {
        transform: scale(0.9);
    }
    100% {
        transform: scale(1);
    }
}

/* Ripple animation */
@keyframes ripple {
    0% {
        transform: scale(1);
        opacity: 0.7;
    }
    100% {
        transform: scale(2.5);
        opacity: 0;
    }
}

.success-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 12px;
}

.success-message {
    font-size: 16px;
    margin: 0;
    line-height: 1.5;
}

.text-center {
    text-align: center;
}

.justify-center {
    justify-content: center;
}
</style>